import { AxiosResponse } from 'axios';
import { useAxios } from '@/features/common/helpers/axios';
import { UserItemType } from '@/features/users/types';
import { parseError } from '@/features/common/helpers/errorParser';
import { GlobalResponseType } from '@/features/common/types';
import i18n from '@/i18n';

const { axiosService } = useAxios();

export interface AddUserDTO {
  firstName: string,
  lastName: string,
  disabled: boolean,
  username: string,
  permissions: {
    adminAccess: boolean,
    viewInvoices: boolean,
    sharedHistory: boolean,
  },
  askUserForDefaultAddress: boolean,
}

export interface EditUserDTO {
  firstName: string,
  lastName: string,
  disabled: boolean,
  permissions: {
    adminAccess: boolean,
    viewInvoices: boolean,
    sharedHistory: boolean,
  },
  askUserForDefaultAddress: boolean,
}

export interface DeleteUserDTO {
  toUserId: number,
  transferTo: string,
  transferFrom: string,
  transferContent: {
    addressBook: boolean,
    partsBook: boolean
  }
}

const filterExceptionMessage = (err: GlobalResponseType): GlobalResponseType => {
  if (!err.errorType) return err;

  const messageKey = `manageUsers.errorMessages.${err.errorType}`;
  if (!i18n.te(messageKey)) return err;

  return {
    ...err,
    errorMessage: i18n.t(messageKey) as string,
  };
};

export const getUsers = async (): Promise<{
  data?: UserItemType[]
} & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.get('/users');
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      data: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const addUser = async (
  data: AddUserDTO,
): Promise<{
  user?: UserItemType
} & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      '/users', data,
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      user: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const editUser = async (
  userId: number,
  data: EditUserDTO,
): Promise<{
  user?: UserItemType
} & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.put(
      `/users/${userId}`, data,
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      user: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const deleteUser = async (
  userId: number,
  data: DeleteUserDTO,
): Promise<{
  deleted?: boolean
} & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.put(
      `/users/${userId}/delete`, data,
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      deleted: !!response.data.deleted,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const resendInvitation = async (
  userId: number,
): Promise<{
  resendInvitation?: boolean
} & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      `/users/${userId}/resend`,
    );
    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      isCarrierMessage: false,
      resendInvitation: !!response.data.resendInvitation,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};
